import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FinalClientService } from '../../../../account/services/final-client.service';
import { ActivatedRoute, Router } from '@angular/router';
import { first, map } from 'rxjs/internal/operators';
import { CountyDTO } from "../../../../shared/models/CountyDTO";
import { CityDTO } from "../../../../shared/models/CityDTO";
import { AddressService } from "../../../../shared/services/address.service";
import { PlaceDetailsGas } from 'src/app/components/account/models/PlaceDetailsGas';

@Component({
  selector: 'app-electricity-place-details',
  templateUrl: './gas-consumption-place-details.component.html',
  styleUrls: ['./gas-consumption-place-details.component.scss']
})
export class GasConsumptionPlaceDetailsComponent implements OnInit, OnDestroy {

  public form: FormGroup;
  public conventionParts: string[] = [];
  public suppliers: string[] = [];
  private routeDataSub: Subscription;
  public placeId: number;
  public isFormSubmitAttempt = false;
  public placeDetails: PlaceDetailsGas;
  public counties: CountyDTO[] = [];
  public cities: CityDTO[] = [];

  @Input()
  public editable: boolean = false;

  @Input()
  set place(place: any) {
    this.form.patchValue(place);
    this.conventionParts = place.conventionParties;
    this.suppliers = place.suppliers;
  }

  @Input()
  public isContractChange: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private finalClientService: FinalClientService,
    private route: ActivatedRoute,
    private router: Router,
    private addressService: AddressService
  ) {
    this.createConsumptionPlaceForm();
  }

  createConsumptionPlaceForm(): void {
    this.form = this.formBuilder.group({
      operatorName: ['', Validators.required],
      code: ['', Validators.required],
      placeType: [''],
      isProsumer: ['', Validators.required],
      conventionParties: [''],
      suppliers: [''],
      //  address
      streetNumber: ['', Validators.required],
      streetName: ['', Validators.required],
      postalCode: ['', Validators.required],
      block: [''],
      apartment: [''],
      floor: [''],
      county: ['', Validators.required],
      city: ['', Validators.required],
      //convention
      conventionType: [''],
      conventionNumber: [''],
      conventionDate: [''],
      conventionPresent: [''],
      //technical data
      debit: ['', Validators.required],
      pressure: ['', Validators.required],
      maximumPressure: ['', Validators.required],
      minimumPressure: ['', Validators.required],
      minimumTechnicalPressure: ['', Validators.required],
      reservedCapacity: ['', Validators.required],
      smartMeter: ['', Validators.required],
      counterType: ['', Validators.required],
      counterSeries: ['', Validators.required],
      technicalDataStatus: ['', Validators.required],
      networkType: ['', Validators.required],
      counterIndexInterval: ['', Validators.required],
      operatingMode: ['', Validators.required],
      consumptionCategory: ['', Validators.required],
      counterIndexDesc: ['', Validators.required],
      counterIndexValue: [''],
      counterIndexReadDate: [''],
      //network contract
      clientIdentificationNumber: [''],
      clientName: [''],
      contractNumber: [''],
      validFrom: [''],
      validTo: [''],
      contractType: [''],

      //connection certificate
      accessAgreement: [''],
      accessAgreementNumber: [''],
      accessAgreementDate: [''],

      //connection notice
      connectionNotice: [''],
      connectionNoticeNumber: [''],
      connectionNoticeDate: [''],
    });
  }

  getFormData(): void {
    if (this.placeId) {
      this.finalClientService.getFCGasConsumptionPlace(this.placeId).pipe(first())
        .subscribe(data => {
          this.form.patchValue(data);
          this.addressService.getCities(this.form.get('county').value).subscribe((cities: CityDTO[]) => this.cities = cities);
        });
    }
  }

  ngOnInit(): void {
    this.addressService.getCounties().subscribe(data => this.counties = data);
    this.routeDataSub = this.route
      .paramMap
      .pipe(
        map(value => {
          if (window.history.state.editable) {
            this.editable = !!window.history.state.editable;
          }
          this.placeId = +window.history.state.placeId;
          return this.placeId;
        })
      ).subscribe((validationDetails) => this.getFormData());
  }

  onSubmit(): void {
    this.isFormSubmitAttempt = true;
  }

  onChangeCounty(): void {
    this.cities = [];
    this.addressService.getCities(this.form.get('county').value).subscribe((cities: CityDTO[]) => this.cities = cities);
  }

  isConventionPresent(): boolean {
    if (this.form.get('conventionPresent').value === null || this.form.get('conventionPresent').value === false) {
      return false;
    } else {
      return true;
    }
  }

  isDirectNetworkContractType(): boolean {
    if (this.suppliers == null) {
      return true;
    } else if (this.suppliers.length > 0) {
      return false;
    } else {
      return true;
    }
  }

  isConsumptionPlaceConnected(): boolean {
    if (this.form.get('technicalDataStatus').value === 'CONECTAT')
      return true;
    else
      return false;
  }

  isConsumptionPlaceDisconnected(): boolean {
    if (this.form.get('technicalDataStatus').value === 'DECONECTAT')
      return true;
    else
      return false;
  }

  isConsumptionPlaceDezafectat(): boolean {
    if (this.form.get('technicalDataStatus').value === 'DEZAFECTAT')
      return true;
    else
      return false;
  }

  isConsumptionPlaceInactive(): boolean {
    if (this.form.get('technicalDataStatus').value === 'INACTIV')
      return true;
    else
      return false;
  }

  isNetworkTypeDistribution(): boolean {
    if (this.form.get('networkType').value === 'D')
      return true;
    else
      return false;
  }

  isNetworkTypeTransport(): boolean {
    if (this.form.get('networkType').value === 'T')
      return true;
    else
      return false;
  }

  isNetworkTypeCA(): boolean {
    if (this.form.get('networkType').value === 'CA')
      return true;
    else
      return false;
  }

  isOperatingModeSeasonal(): boolean {
    if (this.form.get('operatingMode').value === 'SEZONIER')
      return true;
    else
      return false;
  }

  isOperatingModePermanent(): boolean {
    if (this.form.get('operatingMode').value === 'PERMANENT')
      return true;
    else
      return false;
  }

  isSmartMeter(): boolean {
    return this.form.get('smartMeter').value !== null && this.form.get('smartMeter').value;
  }

  isProsumer(): boolean {
    if (this.form.get('isProsumer').value === null || this.form.get('isProsumer').value === false) {
      return false;
    } else {
      return true;
    }
  }

  setTechnicalDataStatus(value): void {
    this.form.controls['technicalDataStatus'].setValue(value);
  }

  setProsumer(value): void {
    this.form.controls['isProsumer'].setValue(value);
  }

  setOperatingMode(value): void {
    this.form.controls['operatingMode'].setValue(value);
  }

  setNetworkType(value): void {
    this.form.controls['networkType'].setValue(value);
  }

  ngOnDestroy(): void {
    this.routeDataSub.unsubscribe();
  }

}
